import React, { useMemo, forwardRef } from 'react';
import { Button, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getFieldIcon } from '../../components/Forecast/icons';
import { WindDirectionIcon } from '../../components/Forecast/WindDirectionIcon';
import type { StationData } from '../../api/stationObservationsService';
import type { ObservationField } from '../../api/observationFieldsService';
import type { EventWithCircuit } from '../../api/eventsService';

dayjs.extend(utc);
dayjs.extend(timezone);

interface StationObservationsPanelProps {
  /** Current theme */
  theme: 'light' | 'dark';
  /** Array of station data */
  stations: StationData[];
  /** Currently selected station index */
  selectedStationIndex: number;
  /** Selected date range */
  selectedDateRange: { start: Date; end: Date } | null;
  /** Selected event data */
  selectedEvent: EventWithCircuit | null;
  /** Card position for dragging */
  cardPosition: { x: number; y: number } | null;
  /** Whether this card is currently being dragged */
  isDragging: boolean;
  /** Handler for mouse down on the card (for dragging) */
  onMouseDown: (e: React.MouseEvent) => void;
  /** Handler to close the panel */
  onClose: () => void;
  /** Handler to change selected station index */
  onStationIndexChange: (index: number) => void;
  /** Function to get visible observation fields */
  getVisibleFields: (includeDemoOnly: boolean) => ObservationField[];
  /** Whether observation fields are loading */
  isLoadingObservationFields: boolean;
  /** Current player/radar frame time — when provided, the panel syncs to this time instead of the range end */
  currentPlayerTime?: Date | null;
}

/**
 * Station Observations Panel
 * 
 * Displays weather station observation data for the selected station and time.
 * Updates as the timeline position changes in the nowcasting player.
 */
export const StationObservationsPanel = forwardRef<HTMLDivElement, StationObservationsPanelProps>(({
  theme,
  stations,
  selectedStationIndex,
  selectedDateRange,
  selectedEvent,
  cardPosition,
  isDragging,
  onMouseDown,
  onClose,
  onStationIndexChange,
  getVisibleFields,
  isLoadingObservationFields,
  currentPlayerTime,
}, ref) => {
  // Check if we should render (must be done after hooks)
  const shouldRender = stations.length > 0 && selectedDateRange !== null && stations[selectedStationIndex] !== undefined;
  
  const station = shouldRender ? stations[selectedStationIndex] : null;
  const targetTime = currentPlayerTime
    ? currentPlayerTime.getTime()
    : selectedDateRange ? new Date(selectedDateRange.end).getTime() : 0;

  // Find observation closest to the selected end date for the selected station
  const closestObs = useMemo(() => {
    if (!station || !station.observations || station.observations.length === 0) {
      return null;
    }
    let closest = station.observations[0];
    let minDiff = Math.abs(new Date(closest.reading_timestamp).getTime() - targetTime);

    station.observations.forEach(obs => {
      const diff = Math.abs(new Date(obs.reading_timestamp).getTime() - targetTime);
      if (diff < minDiff) {
        minDiff = diff;
        closest = obs;
      }
    });

    return closest;
  }, [station, targetTime]);

  // Get available field names from reading_config
  const availableFieldNames = useMemo(() => {
    return station?.reading_config?.map(cfg => cfg.name) ?? [];
  }, [station]);

  // Get visible observation fields, filtered to only those available in reading_config
  const isDemo = selectedEvent?.demo ?? false;
  const visibleFields = useMemo(() => {
    if (!shouldRender) return [];
    return getVisibleFields(isDemo)
      .filter(field => availableFieldNames.includes(field.name))
      .sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));
  }, [getVisibleFields, isDemo, availableFieldNames, shouldRender]);

  // Pair fields into rows (position 1,2 = row 1, position 3,4 = row 2, etc.)
  const rows = useMemo(() => {
    const result: Array<{ left: typeof visibleFields[0] | null; right: typeof visibleFields[0] | null }> = [];
    for (let i = 0; i < visibleFields.length; i += 2) {
      result.push({
        left: visibleFields[i] || null,
        right: visibleFields[i + 1] || null,
      });
    }
    return result;
  }, [visibleFields]);

  // Get the actual observation timestamp for the header
  const observationTime = useMemo(() => {
    if (!closestObs) return null;
    return dayjs(closestObs.reading_timestamp).tz(selectedEvent?.circuit?.time_zone || 'UTC');
  }, [closestObs, selectedEvent?.circuit?.time_zone]);

  // Check if we have all required data for rendering
  const canRender = shouldRender && closestObs !== null && station !== null && observationTime !== null;

  // Extract reading by name using reading_config
  const getReading = (name: string): number | null => {
    const index = safeStation.reading_config.findIndex(cfg => cfg.name === name);
    return index >= 0 ? safeClosestObs.readings[index] : null;
  };

  // Helper to format value with unit
  const formatValue = (field: typeof visibleFields[0], value: number | null): string | React.ReactNode => {
    if (value === null) return 'N/A';

    // Special handling for windDirection - show arrow with exact angle
    if (field.name === 'windDirection') {
      // Use car_wind_direction for display
      const windDir = safeClosestObs.car_wind_direction;
      if (windDir === null || windDir === undefined) return 'N/A';
      
      // Get wind speed for color coding
      const windSpeedValue = getReading('windSpeed');
      const windSpeedKmh = windSpeedValue !== null ? windSpeedValue * 3.6 : null;
      
      return (
        <WindDirectionIcon
          direction={windDir}
          windSpeed={windSpeedKmh}
          size={14}
          showAngle={true}
        />
      );
    }

    // Wind fields: convert m/s to km/h for display
    if (field.name === 'windSpeed' || field.name === 'windGust') {
      const kmhValue = value * 3.6;
      if (field.name === 'windGust' && kmhValue <= 0) return 'N/A';
      return `${kmhValue.toFixed(1)} km/h`;
    }

    // Rain/precipitation: show N/A for zero values
    if ((field.name === 'rainIntensity' || field.name === 'precipitationIntensity') && value === 0) {
      return 'N/A';
    }

    // Format based on unit type
    const unit = field.unit || '';

    // Determine decimal places based on field type
    let decimals = 1;
    if (unit === '%') decimals = 0;
    if (unit === 'lux') decimals = 0;

    return `${value.toFixed(decimals)}${unit ? ` ${unit}` : ''}`;
  };

  // Render a single field cell
  const renderField = (field: typeof visibleFields[0] | null, isRight: boolean) => {
    if (!field) {
      return (
        <>
          <td style={{ padding: isRight ? '2px 0' : '2px 6px 2px 0' }}></td>
          <td style={{ padding: isRight ? '2px 0' : '2px 6px 2px 0' }}></td>
        </>
      );
    }

    const value = getReading(field.name);

    return (
      <>
        <td style={{ padding: isRight ? '2px 6px 2px 0' : '2px 6px 2px 0', whiteSpace: 'nowrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            {getFieldIcon(field.name, { size: 14 })} {field.display_name}
          </span>
        </td>
        <td style={{ padding: isRight ? '2px 0' : '2px 6px 2px 0', textAlign: 'right', fontWeight: 500 }}>
          {formatValue(field, value)}
        </td>
      </>
    );
  };

  // Early return if no stations or no selected station (after all hooks)
  if (!canRender) {
    return null;
  }

  // At this point, TypeScript knows these are non-null due to canRender check
  const safeStation = station!;
  const safeClosestObs = closestObs!;
  const safeObservationTime = observationTime!;

  return (
    <div
      ref={ref}
      onMouseDown={onMouseDown}
      style={{
        position: 'absolute',
        ...(cardPosition
          ? { left: cardPosition.x, top: cardPosition.y }
          : { bottom: 240, left: 24 }),
        backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        padding: '12px 14px',
        borderRadius: 10,
        zIndex: isDragging ? 2000 : 1000,
        minWidth: 400,
        maxWidth: 440,
        border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(10px)',
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      <div
        className="drag-handle"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
          paddingBottom: 6,
          borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          cursor: 'grab',
          userSelect: 'none',
        }}
      >
        {/* Left side: Station label + selector buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 12,
            fontWeight: 600,
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
          }}>
            Station
          </span>
          <div style={{ display: 'flex', gap: 3 }}>
            {stations.map((stationItem, index) => (
              <Button
                key={stationItem.station_reference}
                type={selectedStationIndex === index ? 'primary' : 'default'}
                size="small"
                onClick={() => onStationIndexChange(index)}
                style={{
                  minWidth: 24,
                  height: 20,
                  padding: '0 5px',
                  fontSize: 10,
                }}
              >
                {stationItem.station_reference}
              </Button>
            ))}
          </div>
        </div>
        {/* Right side: Timestamp + close button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 11,
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.55)',
          }}>
            @ {safeObservationTime.format('HH:mm')}
          </span>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
            style={{
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
              minWidth: 24,
              padding: '0 4px',
            }}
          />
        </div>
      </div>

      {/* Station Data */}
      {isLoadingObservationFields ? (
        <Spin size="small" />
      ) : (
        <div style={{
          fontSize: 11,
          lineHeight: 1.5,
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
        }}>
          {/* Weather parameters table */}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {renderField(row.left, false)}
                  {renderField(row.right, true)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

StationObservationsPanel.displayName = 'StationObservationsPanel';

export default StationObservationsPanel;
